import React, { useState } from 'react';
import { Send, X, Check, Mail, Copy } from 'lucide-react';
import SEOHead from '../components/SEO/SEOHead';
import apiService from '../services/apiService';

function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error and success when user starts typing
    setError('');
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate form
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!formData.subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!formData.message.trim()) {
      setError('Please enter your message');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const messageWithContact = `${formData.message.trim()}\n\n---\nContact name: ${formData.name}\nContact email: ${formData.email}`;
      await apiService.sendEmail({ ...formData, message: messageWithContact });
      
      // Show success message
      setSuccess(true);
      
    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/50 relative overflow-hidden">
      <SEOHead 
        title="Contact PricePatent | Patent Valuation Support & Sales"
        description="Contact PricePatent for patent valuation support, enterprise solutions, and IP analytics services. Get expert help with patent assessment, licensing insights, and investment analysis."
        keywords="contact patent valuation, IP analytics support, patent valuation sales, enterprise IP solutions, patent assessment help, intellectual property consultation"
        ogTitle="Contact PricePatent - Patent Valuation Support"
        ogDescription="Get in touch with our patent valuation experts. Professional support for IP analytics, patent assessment, and enterprise solutions."
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact PricePatent",
          "description": "Contact page for PricePatent patent valuation services",
          "mainEntity": {
            "@type": "Organization",
            "name": "PricePatent",
            "email": "pricepatent@wisdomain.com",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "email": "pricepatent@wisdomain.com",
              "areaServed": ["US", "EU", "CN", "JP", "IN"],
              "availableLanguage": ["English"]
            }
          }
        }}
      />
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full -translate-x-48 -translate-y-48"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tl from-indigo-200/25 to-blue-200/25 rounded-full translate-x-40 translate-y-40"></div>
      <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-gradient-to-br from-purple-200/15 to-pink-200/15 rounded-full"></div>
      
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h1>
          <p className="text-xl text-gray-600">Get in touch with our team</p>
        </div>

        <div className="space-y-8">
          {/* Email Card - Primary */}
          <div className="relative text-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-xl p-8 border border-blue-100 overflow-hidden shadow-sm">
            {/* Background decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200/30 to-purple-200/30 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-200/30 to-blue-200/30 rounded-full translate-y-12 -translate-x-12"></div>
            
            {/* Content */}
            <div className="relative z-10">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Email Us Directly
              </h3>
              <p className="text-gray-700 mb-6 max-w-md mx-auto">
                Send us a message directly for immediate assistance<br />
                and personalized support
              </p>
              <div className="space-y-4">
                <a 
                  href="mailto:pricepatent@wisdomain.com" 
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  pricepatent@wisdomain.com
                </a>
                <div>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText('pricepatent@wisdomain.com');
                      // Could add a toast notification here in future
                    }}
                    className="text-sm text-gray-600 hover:text-gray-800 bg-white/60 hover:bg-white/80 border border-gray-300 rounded-lg px-4 py-2 transition-all duration-200 inline-flex items-center backdrop-blur-sm"
                  >
                    <Copy className="h-3 w-3 mr-2" />
                    Copy Email Address
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Divider with "OR" */}
          <div className="flex items-center">
            <div className="flex-1 border-t border-gray-300"></div>
            <span className="px-4 text-sm text-gray-500 bg-gray-50">OR</span>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          {/* Contact Form Card - Secondary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Use Our Contact Form</h3>
              <p className="text-gray-600">Fill out the form below and we'll get back to you soon</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`mt-1 block w-full rounded-md border shadow-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 py-3 px-4 text-base bg-white ${
                      error && !formData.name.trim() ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`mt-1 block w-full rounded-md border shadow-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 py-3 px-4 text-base bg-white ${
                      error && (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                  Subject
                </label>
                <input
                  type="text"
                  name="subject"
                  id="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md border shadow-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 py-3 px-4 text-base bg-white ${
                    error && !formData.subject.trim() ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  name="message"
                  id="message"
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className={`mt-1 block w-full rounded-md border shadow-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 py-3 px-4 text-base bg-white ${
                    error && !formData.message.trim() ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                  }`}
                />
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <X className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              {success && (
                <div className="rounded-md bg-green-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Check className="h-5 w-5 text-green-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">Message sent successfully!</h3>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700'
                  } transition-all duration-300`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactPage; 